<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { UseCaseArticle, UseCaseArticleChapter } from '@/data/useCases'
import ArticleContents, { type ContentsGroup } from './ArticleContents.vue'
import ArticleLightbox from './ArticleLightbox.vue'
import ArticleRecipeTile from './ArticleRecipeTile.vue'
import ArticleZoom from './ArticleZoom.vue'
import ArticlePrompt from './ArticlePrompt.vue'
import ArticleRecipe from './ArticleRecipe.vue'
import ArticleTable from './ArticleTable.vue'
import ArticleBudget from './ArticleBudget.vue'
import { formatTokens, poundsFor } from '@/composables/articleLabel'

const props = defineProps<{ article: UseCaseArticle }>()

const tokenLabel = (n: number) => (n === 0 ? 'Free' : `${formatTokens(n)} tokens`)

// With a budget, a token figure also reads as pounds at that pack's rate.
const pack = computed(() => props.article.budget?.pack)
const pounds = (n: number) => (pack.value ? poundsFor(n, pack.value) : '')
// Chips stay in tokens, the unit the app's Make button shows; pounds live in the
// budget panel, the rail and the mobile bar.
const spentLabel = (n: number) => (n === 0 || !pack.value ? tokenLabel(n) : `${tokenLabel(n)} · ${pounds(n)}`)
const barPounds = (n: number) => (pack.value ? poundsFor(n, pack.value, { approx: false }) : '')

const costTable = computed(() => {
  const rows = props.article.costRows
  const total = rows.reduce((sum, r) => sum + r.tokens, 0)
  const withPounds = !!pack.value
  return {
    columns: withPounds ? ['Item', 'Tokens', 'Pounds'] : ['Item', 'Tokens'],
    rows: rows.map((r) => [r.item, formatTokens(r.tokens), ...(withPounds ? [pounds(r.tokens)] : [])]),
    subs: rows.map((r) => r.detail),
    total: { label: 'Total', values: [formatTokens(total), ...(withPounds ? [pounds(total)] : [])] },
  }
})

// Chapters keep one flat 1..n numbering; parts only group them. An article without
// parts renders as a single ungrouped list.
const groups = computed(() => {
  const numbered = props.article.chapters.map((chapter, i) => ({ chapter, number: i + 1 }))
  const parts = props.article.parts
  if (!parts?.length) return [{ id: 'all', title: undefined, summary: '', spent: 0, chapters: numbered }]
  return parts.map((part) => {
    const chapters = numbered.filter((n) => part.chapterIds.includes(n.chapter.id))
    return {
      id: part.id,
      title: part.title as string | undefined,
      summary: part.summary,
      spent: chapters.reduce((sum, n) => sum + (n.chapter.spent ?? 0), 0),
      chapters,
    }
  })
})
// A chapter with featured recipes shows those as full cards and the rest as a tile
// grid; one with none flagged shows every recipe as a card.
function recipeSplit(chapter: UseCaseArticleChapter) {
  const all = chapter.recipes ?? []
  const featured = all.filter((r) => r.featured)
  return featured.length ? { cards: featured, tiles: all.filter((r) => !r.featured) } : { cards: all, tiles: [] }
}
const contentsGroups = computed<ContentsGroup[]>(() => groups.value)
const costNumber = computed(() => props.article.chapters.length + 1)

// Which chapter the reader is in, so the rail, the progress bar and the dropdown all
// say where they are: the last heading whose top has passed the sticky bar, kept for
// as long as that chapter scrolls. Computed on scroll in onMounted: vite-ssg
// prerenders this component, and nothing here may touch window before then.
const activeId = ref<string | null>(null)
const activeIndex = computed(() => props.article.chapters.findIndex((c) => c.id === activeId.value))
const barLabel = computed(() => {
  const total = props.article.chapters.length
  if (activeId.value === 'article-cost') return 'What it cost'
  const chapter = props.article.chapters[activeIndex.value]
  if (!chapter) return `${total} steps`
  return `Step ${activeIndex.value + 1} of ${total} · ${chapter.short ?? chapter.title}`
})
// Chapters read so far, the one in view included; the cost section counts as the end.
const reached = computed(() =>
  activeId.value === 'article-cost' ? props.article.chapters.length : Math.max(0, activeIndex.value + 1),
)
// Running total: everything spent up to and including the chapter being read.
const spentSoFar = computed(() =>
  props.article.chapters.slice(0, reached.value).reduce((sum, c) => sum + (c.spent ?? 0), 0),
)
const progress = computed(() => (reached.value / props.article.chapters.length) * 100)

const menuOpen = ref(false)
let frame = 0
let headings: HTMLElement[] = []
// Header (~64px) plus the bar (~46px) on mobile; the rail only needs the header.
const BAR_BOTTOM = 130

function update() {
  frame = 0
  let current: string | null = null
  for (const h of headings) {
    if (h.getBoundingClientRect().top <= BAR_BOTTOM) current = h.id
    else break
  }
  activeId.value = current
}
function onScroll() {
  if (!frame) frame = requestAnimationFrame(update)
}

onMounted(() => {
  const ids = [...props.article.chapters.map((c) => c.id), 'article-cost']
  headings = ids.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el)
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll, { passive: true })
  update()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onScroll)
  if (frame) cancelAnimationFrame(frame)
})
</script>

<template>
  <!-- Long-form how-to, only on entries that declare `article`. Sits right after
       the film so the finished thing comes first, then the full workflow with its
       pictures, prompts and cost. A sticky contents rail at lg and up; below lg a
       sticky progress bar with a dropdown of the same list. -->
  <section class="border-t border-surface-800 py-14 md:py-20 px-6 sm:px-8 lg:px-10 bg-surface-950">
    <div class="max-w-6xl mx-auto">
      <h2 class="text-2xl sm:text-3xl font-bold text-surface-50 mb-4">{{ article.heading }}</h2>
      <p v-if="article.intro" class="max-w-[68ch] text-base text-surface-300 leading-relaxed mb-8">{{ article.intro }}</p>

      <ArticleBudget v-if="article.budget" :budget="article.budget" :groups="groups" />

      <nav id="article-contents" aria-label="The steps at a glance" class="scroll-mt-32 lg:scroll-mt-24 mb-8">
        <ol class="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 sm:grid sm:grid-cols-4 sm:overflow-visible sm:pb-0 xl:grid-cols-8">
          <li v-for="(chapter, i) in article.chapters" :key="chapter.id" class="w-[calc((100%-1.5rem)/2.42)] shrink-0 snap-start sm:w-auto">
            <a :href="`#${chapter.id}`" class="block h-full rounded-card border border-surface-800 bg-surface-900 p-2 hover:border-surface-600 transition-colors">
              <img v-if="chapter.thumb" :src="chapter.thumb" alt="" loading="lazy" class="block w-full aspect-video object-cover rounded-lg bg-black" />
              <span class="mt-2 flex items-baseline gap-1.5 text-sm font-medium text-surface-50">
                <span class="font-mono text-primary-300">{{ i + 1 }}</span>
                <span>{{ chapter.short ?? chapter.title }}</span>
              </span>
              <span v-if="chapter.spent !== undefined" class="mt-1 inline-block rounded-full bg-surface-800 px-2 py-0.5 text-xs text-brass-400">{{ tokenLabel(chapter.spent) }}</span>
            </a>
          </li>
        </ol>
      </nav>

      <!-- Below lg: where you are, and a way to jump. The fixed site header is about 64px. -->
      <div class="lg:hidden sticky top-16 z-30 -mx-6 sm:-mx-8 mb-8 border-b border-surface-800 bg-surface-950">
        <div class="flex items-center justify-between gap-3 px-6 sm:px-8">
          <p class="min-w-0 flex-1 text-sm text-surface-200 truncate">{{ barLabel }}</p>
          <span v-if="pack && spentSoFar > 0" class="shrink-0 text-sm text-brass-400">{{ barPounds(spentSoFar) }} spent</span>
          <button
            type="button"
            class="shrink-0 inline-flex items-center gap-1.5 min-h-11 text-sm text-primary-300"
            :aria-expanded="menuOpen"
            aria-controls="article-contents-menu"
            @click="menuOpen = !menuOpen"
            @keydown.esc="menuOpen = false"
          >
            Contents
            <svg class="w-4 h-4 transition-transform" :class="{ 'rotate-180': menuOpen }" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l5 5 5-5" /></svg>
          </button>
        </div>
        <div class="h-0.5 bg-surface-800" aria-hidden="true"><div class="h-full bg-primary-300 transition-[width]" :style="{ width: `${progress}%` }" /></div>
        <div v-show="menuOpen" id="article-contents-menu" class="absolute inset-x-0 top-full max-h-[70vh] overflow-y-auto border-b border-surface-800 bg-surface-900 px-6 sm:px-8 py-3 shadow-panel">
          <ArticleContents :groups="contentsGroups" :active-id="activeId" :cost-number="costNumber" @navigate="menuOpen = false" />
        </div>
      </div>

      <div class="lg:grid lg:grid-cols-[14rem_minmax(0,1fr)] gap-12">
        <aside class="hidden lg:block" aria-label="In this article">
          <div class="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <div v-if="pack" class="mb-4 pl-3">
              <p class="text-xs uppercase tracking-wide text-surface-400">Spent so far</p>
              <p class="text-sm font-semibold text-brass-400">{{ spentSoFar ? spentLabel(spentSoFar) : 'Nothing yet' }}</p>
            </div>
            <ArticleContents :groups="contentsGroups" :active-id="activeId" :cost-number="costNumber" />
          </div>
        </aside>

        <div class="min-w-0">
          <section v-for="group in groups" :key="group.id" class="mb-14">
            <header v-if="group.title" class="mb-8 border-b border-surface-800 pb-4">
              <div class="flex items-baseline justify-between gap-4">
                <h3 :id="group.id" class="scroll-mt-32 lg:scroll-mt-24 text-2xl sm:text-3xl font-bold text-surface-50">{{ group.title }}</h3>
                <span class="shrink-0 text-sm font-medium text-brass-400">{{ tokenLabel(group.spent) }}</span>
              </div>
              <p class="mt-2 max-w-[68ch] text-base text-surface-300">{{ group.summary }}</p>
            </header>

            <article v-for="{ chapter, number } in group.chapters" :key="chapter.id" class="mb-14 last:mb-0">
              <header class="mb-5">
                <h4 :id="chapter.id" class="scroll-mt-32 lg:scroll-mt-24 flex items-center gap-3 text-xl sm:text-2xl font-bold text-surface-50">
                  <span class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-300 text-surface-950 text-base font-bold">{{ number }}</span>
                  <span>{{ chapter.title }}</span>
                </h4>
                <p v-if="chapter.summary" class="mt-3 max-w-[68ch] text-lg sm:text-xl text-surface-100 leading-snug">{{ chapter.summary }}</p>
                <div v-if="chapter.tool || chapter.settings?.length || chapter.spent !== undefined || chapter.cost" class="mt-3 space-y-2">
                  <div class="flex flex-wrap items-center gap-2">
                    <p v-if="chapter.tool" class="inline-flex items-center gap-1.5 rounded-full border border-primary-300/50 bg-surface-900 text-primary-300 text-xs sm:text-sm font-medium px-3 py-1">
                      <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10h13M11 5l5 5-5 5" /></svg>
                      {{ chapter.tool }}
                    </p>
                    <span v-for="setting in chapter.settings" :key="setting" class="rounded-full bg-surface-800 text-surface-200 text-xs sm:text-sm px-3 py-1">{{ setting }}</span>
                    <span v-if="chapter.spent !== undefined" class="rounded-full bg-surface-800 text-brass-400 text-xs sm:text-sm font-medium px-3 py-1">{{ tokenLabel(chapter.spent) }}</span>
                  </div>
                  <p v-if="chapter.cost" class="text-sm text-surface-400">{{ chapter.cost }}</p>
                </div>
              </header>

              <p v-for="(para, pIdx) in chapter.paragraphs" :key="pIdx" class="max-w-[68ch] text-base text-surface-300 leading-relaxed mb-4">{{ para }}</p>

              <ol v-if="chapter.rules?.length" class="my-6 grid gap-3 sm:grid-cols-3">
                <li v-for="(rule, rIdx) in chapter.rules" :key="rule.title" class="rounded-card border border-surface-800 bg-surface-900 p-4">
                  <span class="block text-sm font-mono text-surface-400 leading-none mb-2">{{ rIdx + 1 }}</span>
                  <p class="text-sm font-semibold text-surface-50 mb-1">{{ rule.title }}</p>
                  <p class="text-sm text-surface-300 leading-relaxed">{{ rule.text }}</p>
                </li>
              </ol>

              <ol v-if="chapter.actions?.length" class="my-6 space-y-4">
                <li v-for="(action, aIdx) in chapter.actions" :key="aIdx" class="flex gap-4 items-start">
                  <span class="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border border-surface-600 text-sm text-surface-200">{{ aIdx + 1 }}</span>
                  <p class="max-w-[68ch] text-base text-surface-100 leading-relaxed">{{ action }}</p>
                </li>
              </ol>

              <div v-if="chapter.callouts?.length" class="my-6 grid gap-3 sm:grid-cols-2">
                <div v-for="callout in chapter.callouts" :key="callout.title" class="rounded-card border border-surface-700 bg-surface-900 p-4">
                  <p class="text-xs uppercase tracking-wide text-surface-400 mb-1">{{ callout.title }}</p>
                  <p class="text-sm text-surface-200 leading-relaxed">{{ callout.text }}</p>
                </div>
              </div>

              <div v-if="chapter.images?.length" class="my-6 grid gap-4" :class="{ 'sm:grid-cols-2 items-start': chapter.images.length > 1 }">
                <figure v-for="img in chapter.images" :key="img.src">
                  <div class="rounded-card overflow-hidden border border-surface-700 bg-black">
                    <ArticleZoom :item="{ kind: 'image', ...img }" :label="img.alt">
                      <img :src="img.src" :alt="img.alt" loading="lazy" class="block w-full h-auto" />
                    </ArticleZoom>
                  </div>
                  <figcaption v-if="img.caption" class="mt-2 text-sm text-surface-400">{{ img.caption }}</figcaption>
                </figure>
              </div>

              <details v-for="(prompt, prIdx) in chapter.prompts" :key="prIdx" name="article-recipe-prompt" class="group my-6">
                <summary class="cursor-pointer inline-flex items-center gap-1.5 min-h-11 text-sm text-primary-300">
                  {{ prompt.label }}
                  <svg class="w-4 h-4 shrink-0 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l5 5 5-5" /></svg>
                </summary>
                <ArticlePrompt class="mt-1" :aria-name="prompt.label" :text="prompt.text" />
              </details>

              <div v-if="chapter.recipes?.length" class="my-6 flex flex-col gap-6">
                <ArticleRecipe v-for="(recipe, rIdx) in recipeSplit(chapter).cards" :key="recipe.label" :recipe="recipe" :index="rIdx + 1" :total="recipeSplit(chapter).cards.length" />
                <ul v-if="recipeSplit(chapter).tiles.length" class="grid grid-cols-2 gap-3 md:grid-cols-3">
                  <li v-for="recipe in recipeSplit(chapter).tiles" :key="recipe.label"><ArticleRecipeTile :recipe="recipe" /></li>
                </ul>
              </div>

              <figure v-if="chapter.table" class="my-6">
                <figcaption v-if="chapter.table.caption" class="mb-2 text-sm text-surface-400">{{ chapter.table.caption }}</figcaption>
                <ArticleTable :columns="chapter.table.columns" :rows="chapter.table.rows" nowrap-first />
              </figure>
            </article>
          </section>

          <h3 id="article-cost" class="scroll-mt-32 lg:scroll-mt-24 text-xl sm:text-2xl font-bold text-surface-50 mb-4">What it cost</h3>
          <ArticleTable v-bind="costTable" class="lg:max-w-2xl" compact :numeric-from="1" :money-col="pack ? 2 : undefined" />
          <p class="mt-4 max-w-[68ch] text-base text-surface-300 leading-relaxed">{{ article.costNote }}</p>
        </div>
      </div>
    </div>
    <ArticleLightbox />
  </section>
</template>
